import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, Typography, Radio, Space, Button, Tooltip,
  Row, Col, Slider, Checkbox, Divider, Tag, message
} from 'antd';
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  ZoomOutOutlined,
  ReloadOutlined,
  DragOutlined,
  PushpinOutlined
} from '@ant-design/icons';
import * as d3 from 'd3';
import { CHART_DIMENSIONS, NODE_STYLES, LINK_STYLES, D3_FORCE_CONFIG, SVG_STYLES } from '../config/chartStyles';

const { Title, Paragraph } = Typography;

// 中文映射
const chineseNames: Record<string, string> = {
  'Customer': '客户',
  'Order': '订单',
  'Shipment': '运输单',
  'Cargo': '货物',
  'Route': '路线',
  'Quotation': '报价单',
  'ChargeItem': '费用项',
  'FreightCalculation': '运费计算服务',
  'ShipmentRepository': '运输单数据库访问层'
};

const typeLabels: Record<string, string> = {
  'aggregate': '聚合根',
  'entity': '实体',
  'valueObject': '值对象',
  'service': '领域服务',
  'repository': '数据库访问层'
};

// 类型定义
interface Entity {
  id: string;
  name: string;
  type: 'aggregate' | 'entity' | 'valueObject' | 'service' | 'repository';
  attributes?: Attribute[];
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
  vx?: number;
  vy?: number;
}

interface Attribute {
  name: string;
  type: string;
  required?: boolean;
  description?: string;
}

interface Relationship {
  source: string;
  target: string;
  type: '1:1' | '1:N' | 'N:N';
  name: string;
}

const EnhancedEntityDiagram: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 视图控制状态
  const [viewMode, setViewMode] = useState<'simple' | 'detailed'>('detailed');
  const [layoutMode, setLayoutMode] = useState<'force' | 'manual'>('force');
  const [showAttributes, setShowAttributes] = useState(true);
  const [showRelationshipLabels, setShowRelationshipLabels] = useState(true);
  const [nodeSize, setNodeSize] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [animationEnabled, setAnimationEnabled] = useState(true);
  
  // 实体数据
  const entities: Entity[] = [
    {
      id: 'customer',
      name: 'Customer',
      type: 'entity',
      attributes: [
        { name: 'customerId', type: 'string', required: true, description: '客户编号' },
        { name: 'name', type: 'string', required: true, description: '客户名称' },
        { name: 'creditLimit', type: 'number', required: true, description: '信用额度' },
        { name: 'type', type: 'enum', required: true, description: '客户类型' }
      ],
      x: 200,
      y: 200
    },
    {
      id: 'order',
      name: 'Order',
      type: 'aggregate',
      attributes: [
        { name: 'orderId', type: 'string', required: true, description: '订单编号' },
        { name: 'orderDate', type: 'date', required: true, description: '下单日期' },
        { name: 'totalAmount', type: 'Money', required: true, description: '总金额' },
        { name: 'status', type: 'enum', required: true, description: '订单状态' }
      ],
      x: 400,
      y: 200
    },
    {
      id: 'shipment',
      name: 'Shipment',
      type: 'entity',
      attributes: [
        { name: 'shipmentNo', type: 'string', required: true, description: '运输单号' },
        { name: 'status', type: 'enum', required: true, description: '运输状态' },
        { name: 'eta', type: 'date', required: false, description: '预计到达时间' }
      ]
    },
    {
      id: 'cargo',
      name: 'Cargo',
      type: 'entity',
      attributes: [
        { name: 'description', type: 'string', required: true, description: '货物描述' },
        { name: 'weight', type: 'number', required: true, description: '重量' },
        { name: 'volume', type: 'number', required: true, description: '体积' }
      ]
    },
    {
      id: 'route',
      name: 'Route',
      type: 'valueObject',
      attributes: [
        { name: 'origin', type: 'Port', required: true, description: '起运港' },
        { name: 'destination', type: 'Port', required: true, description: '目的港' },
        { name: 'transhipment', type: 'Port[]', required: false, description: '中转港' }
      ]
    },
    {
      id: 'quotation',
      name: 'Quotation',
      type: 'entity',
      attributes: [
        { name: 'quotationNo', type: 'string', required: true, description: '报价单号' },
        { name: 'validUntil', type: 'date', required: true, description: '有效期至' },
        { name: 'totalAmount', type: 'Money', required: true, description: '总金额' }
      ]
    },
    {
      id: 'chargeItem',
      name: 'ChargeItem',
      type: 'valueObject',
      attributes: [
        { name: 'chargeCode', type: 'string', required: true, description: '费用代码' },
        { name: 'chargeName', type: 'string', required: true, description: '费用名称' },
        { name: 'amount', type: 'Money', required: true, description: '金额' }
      ]
    },
    {
      id: 'freightService',
      name: 'FreightCalculation',
      type: 'service',
      attributes: []
    },
    {
      id: 'shipmentRepo',
      name: 'ShipmentRepository',
      type: 'repository',
      attributes: []
    }
  ];

  const relationships: Relationship[] = [
    { source: 'customer', target: 'order', type: '1:N', name: '下单' },
    { source: 'order', target: 'shipment', type: '1:1', name: '生成' },
    { source: 'order', target: 'cargo', type: '1:N', name: '包含' },
    { source: 'order', target: 'quotation', type: '1:1', name: '报价' },
    { source: 'quotation', target: 'chargeItem', type: '1:N', name: '费用明细' },
    { source: 'shipment', target: 'route', type: '1:1', name: '使用' },
    { source: 'freightService', target: 'order', type: '1:N', name: '计算' },
    { source: 'shipmentRepo', target: 'shipment', type: '1:N', name: '存储' }
  ];

  // 获取节点颜色
  const getNodeColor = (type: string) => {
    switch (type) {
      case 'aggregate': return '#1890ff';
      case 'entity': return '#52c41a';
      case 'valueObject': return '#faad14';
      case 'service': return '#fa8c16';
      case 'repository': return '#eb2f96';
      default: return '#666';
    }
  };

  // 绘制图表
  const drawDiagram = useCallback(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = containerRef.current?.clientWidth || 1200;
    const height = 800;
    
    svg.attr('width', width).attr('height', height);
    
    // 简单的中心到中心连接（第一版设计）
    const getNodeCenter = (node: any) => {
      return {
        x: node.x || 0,
        y: node.y || 0
      };
    };
    
    // 初始化实体位置（如果没有设置）
    entities.forEach((entity, i) => {
      if (!entity.x || !entity.y) {
        const cols = 3;
        entity.x = 200 + (i % cols) * 300;
        entity.y = 200 + Math.floor(i / cols) * 200;
      }
    });

    // 添加白色背景
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'white');
    
    // 创建缩放容器
    const g = svg.append('g');

    // 添加缩放行为
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoom(event.transform.k);
      });

    svg.call(zoomBehavior as any);

    // 创建箭头标记
    const defs = svg.append('defs');
    
    // 创建统一大小的箭头
    const arrowSize = 8;
    const refX = 10; // 调整箭头位置，因为现在连线是从框边开始的
    
    // 清除旧的marker
    defs.selectAll('marker').remove();
    
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', refX)
      .attr('refY', 0)
      .attr('markerWidth', arrowSize)
      .attr('markerHeight', arrowSize)
      .attr('orient', 'auto')
      .attr('markerUnits', 'userSpaceOnUse')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#1890ff')
      .attr('stroke', '#1890ff')
      .attr('stroke-width', 1);

    // 创建不同的图层（注意顺序：节点在底部，连线在中间，标签在顶部）
    const nodeGroup = g.append('g').attr('class', 'nodes');
    const linkGroup = g.append('g').attr('class', 'links');
    const labelGroup = g.append('g').attr('class', 'labels'); // 标签层在最上面
    

    // 直接绘制线条，不使用嵌套的g元素
    const lines = linkGroup.selectAll('line')
      .data(relationships)
      .enter().append('line')
      .attr('stroke', '#1890ff')  // 使用更明显的蓝色
      .attr('stroke-opacity', 1)    // 完全不透明
      .attr('stroke-width', 2)      // 适中的线宽
      .attr('marker-end', 'url(#arrowhead)')
      .style('fill', 'none')
      .style('pointer-events', 'none');
    
    // 调试：输出连线数量
    console.log('Created lines:', lines.size(), 'for relationships:', relationships.length);
    
    // 设置初始连线位置（中心到中心）
    lines
      .attr('x1', (d: any) => {
        const source = entities.find(e => e.id === d.source);
        return source?.x || 0;
      })
      .attr('y1', (d: any) => {
        const source = entities.find(e => e.id === d.source);
        return source?.y || 0;
      })
      .attr('x2', (d: any) => {
        const target = entities.find(e => e.id === d.target);
        return target?.x || 0;
      })
      .attr('y2', (d: any) => {
        const target = entities.find(e => e.id === d.target);
        return target?.y || 0;
      });

    // 创建节点（先绘制节点，后绘制标签）
    
    const nodes = nodeGroup.selectAll('g')
      .data(entities)
      .enter().append('g')
      .attr('transform', d => `translate(${d.x || 0}, ${d.y || 0})`)
      .attr('cursor', 'pointer')
      .on('click', (event, d) => {
        setSelectedEntity(d.id === selectedEntity ? null : d.id);
      });

    if (viewMode === 'simple') {
      // 简单视图 - 圆形节点
      nodes.append('circle')
        .attr('r', nodeSize / 2)
        .attr('fill', d => getNodeColor(d.type))
        .attr('fill-opacity', 0.8)
        .attr('stroke', 'white')
        .attr('stroke-width', 3);

      nodes.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', -5)
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('fill', 'white')
        .text(d => d.name);

      nodes.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', 10)
        .attr('font-size', '12px')
        .attr('fill', 'white')
        .text(d => chineseNames[d.name] || d.name);
    } else {
      // 详细视图 - 卡片节点
      nodes.each(function(d) {
        const node = d3.select(this);
        const attributes = showAttributes ? (d.attributes || []) : [];
        const height = 80 + attributes.length * 18;
        const width = 200;

        // 背景矩形
        node.append('rect')
          .attr('x', -width/2)
          .attr('y', -40)
          .attr('width', width)
          .attr('height', height)
          .attr('rx', 5)
          .attr('fill', 'white')
          .attr('stroke', getNodeColor(d.type))
          .attr('stroke-width', d.id === selectedEntity ? 3 : 2)
          .style('filter', d.id === selectedEntity ? 'drop-shadow(0 0 10px rgba(0,0,0,0.3))' : '');

        // 标题区域
        node.append('rect')
          .attr('x', -width/2)
          .attr('y', -40)
          .attr('width', width)
          .attr('height', 40)
          .attr('rx', 5)
          .attr('fill', getNodeColor(d.type))
          .attr('fill-opacity', 0.1);

        // 英文名
        node.append('text')
          .attr('x', 0)
          .attr('y', -20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '16px')
          .attr('font-weight', 'bold')
          .attr('fill', getNodeColor(d.type))
          .text(d.name);

        // 中文名
        node.append('text')
          .attr('x', 0)
          .attr('y', -5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '14px')
          .attr('fill', getNodeColor(d.type))
          .text(chineseNames[d.name] || d.name);

        // 类型标签
        node.append('text')
          .attr('x', 0)
          .attr('y', 10)
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('fill', '#999')
          .text(`<<${typeLabels[d.type]}>>`);

        // 分隔线
        if (showAttributes && attributes.length > 0) {
          node.append('line')
            .attr('x1', -width/2 + 10)
            .attr('x2', width/2 - 10)
            .attr('y1', 20)
            .attr('y2', 20)
            .attr('stroke', '#e8e8e8');

          // 属性列表
          attributes.forEach((attr, i) => {
            const y = 35 + i * 18;
            
            // 属性名
            node.append('text')
              .attr('x', -width/2 + 10)
              .attr('y', y)
              .attr('font-size', '12px')
              .attr('fill', '#333')
              .text(attr.name + ':');

            // 属性类型或描述
            const displayText = attr.description || attr.type;
            node.append('text')
              .attr('x', -width/2 + 80)
              .attr('y', y)
              .attr('font-size', '12px')
              .attr('fill', '#666')
              .text(displayText);

            // 必填标记
            if (attr.required) {
              node.append('text')
                .attr('x', width/2 - 15)
                .attr('y', y)
                .attr('font-size', '12px')
                .attr('fill', '#ff4d4f')
                .text('*');
            }
          });
        }
      });
    }

    // 关系标签（在节点之后绘制，确保在最上层）
    if (showRelationshipLabels) {
      const linkLabels = labelGroup.selectAll('g.link-label')
        .data(relationships)
        .enter().append('g')
        .attr('class', 'link-label');
      
      linkLabels.each(function(d: any) {
        const source = entities.find(e => e.id === d.source);
        const target = entities.find(e => e.id === d.target);
        if (source && target) {
          const midX = (source.x! + target.x!) / 2;
          const midY = (source.y! + target.y!) / 2;
          
          const g = d3.select(this);
          
          // 添加一个稍微偏移的位置，避免标签完全在线的中心
          const angle = Math.atan2(target.y! - source.y!, target.x! - source.x!);
          const offsetX = Math.sin(angle) * 15;
          const offsetY = -Math.cos(angle) * 15;
          
          g.append('rect')
            .attr('x', midX + offsetX - 45)
            .attr('y', midY + offsetY - 12)
            .attr('width', 90)
            .attr('height', 24)
            .attr('fill', 'white')
            .attr('fill-opacity', 0.9)
            .attr('stroke', '#999')
            .attr('stroke-width', 1)
            .attr('rx', 3);
          
          g.append('text')
            .attr('x', midX + offsetX)
            .attr('y', midY + offsetY + 4)
            .attr('text-anchor', 'middle')
            .attr('font-size', '12px')
            .attr('font-weight', 'normal')
            .attr('fill', '#333')
            .text(`${d.name} (${d.type})`);
        }
      });
    }

    // 设置力导向模拟
    if (layoutMode === 'force') {
      const simulation = d3.forceSimulation(entities as any)
        .force('link', d3.forceLink(relationships)
          .id((d: any) => d.id)
          .distance(viewMode === 'simple' ? 150 : 250))
        .force('charge', d3.forceManyBody().strength(viewMode === 'simple' ? -300 : -500))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('collision', d3.forceCollide().radius(viewMode === 'simple' ? nodeSize : 120));

      // 拖拽行为
      const drag = d3.drag()
        .on('start', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d: any) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0);
          // 在力导向模式下，拖拽结束后释放固定
          d.fx = null;
          d.fy = null;
        });

      nodes.call(drag as any);

      // 更新位置
      simulation.on('tick', () => {
        // 简化连线更新 - 直接使用节点中心（第一版设计）
        lines
          .attr('x1', (d: any) => (typeof d.source === 'object' ? d.source.x : entities.find(e => e.id === d.source)?.x) || 0)
          .attr('y1', (d: any) => (typeof d.source === 'object' ? d.source.y : entities.find(e => e.id === d.source)?.y) || 0)
          .attr('x2', (d: any) => (typeof d.target === 'object' ? d.target.x : entities.find(e => e.id === d.target)?.x) || 0)
          .attr('y2', (d: any) => (typeof d.target === 'object' ? d.target.y : entities.find(e => e.id === d.target)?.y) || 0);

        // 更新关系标签位置
        if (showRelationshipLabels) {
          labelGroup.selectAll('g.link-label').each(function(d: any) {
            let sourceX, sourceY, targetX, targetY;
            
            // 处理d3.forceLink转换后的对象
            if (typeof d.source === 'object') {
              sourceX = d.source.x;
              sourceY = d.source.y;
            } else {
              const source = entities.find(e => e.id === d.source);
              sourceX = source?.x || 0;
              sourceY = source?.y || 0;
            }
            
            if (typeof d.target === 'object') {
              targetX = d.target.x;
              targetY = d.target.y;
            } else {
              const target = entities.find(e => e.id === d.target);
              targetX = target?.x || 0;
              targetY = target?.y || 0;
            }
            
            const midX = (sourceX + targetX) / 2;
            const midY = (sourceY + targetY) / 2;
            
            // 计算偏移，避免标签在线的正中心
            const angle = Math.atan2(targetY - sourceY, targetX - sourceX);
            const offsetX = Math.sin(angle) * 15;
            const offsetY = -Math.cos(angle) * 15;
            
            const g = d3.select(this);
            
            g.select('rect')
              .attr('x', midX + offsetX - 45)
              .attr('y', midY + offsetY - 12);
            
            g.select('text')
              .attr('x', midX + offsetX)
              .attr('y', midY + offsetY + 4);
          });
        }

        nodes.attr('transform', (d: any) => `translate(${d.x}, ${d.y})`);
      });

      // 如果启用动画，让力导向图运行一段时间
      if (animationEnabled) {
        simulation.alpha(1).restart();
      } else {
        // 快速稳定
        simulation.stop();
        for (let i = 0; i < 300; ++i) simulation.tick();
        // 触发最终位置更新
        nodes.attr('transform', (d: any) => `translate(${d.x}, ${d.y})`);
      }
    } else {
      // 手动布局模式 - 使用网格布局
      const cols = Math.ceil(Math.sqrt(entities.length));
      entities.forEach((entity, i) => {
        if (!entity.fx && !entity.fy) {
          entity.x = 150 + (i % cols) * 250;
          entity.y = 150 + Math.floor(i / cols) * 200;
        } else {
          entity.x = entity.fx || entity.x;
          entity.y = entity.fy || entity.y;
        }
      });

      // 应用位置
      nodes.attr('transform', d => `translate(${d.x}, ${d.y})`);
      
      // 更新连线（简化为中心到中心）
      lines
        .attr('x1', (d: any) => entities.find(e => e.id === d.source)?.x || 0)
        .attr('y1', (d: any) => entities.find(e => e.id === d.source)?.y || 0)
        .attr('x2', (d: any) => entities.find(e => e.id === d.target)?.x || 0)
        .attr('y2', (d: any) => entities.find(e => e.id === d.target)?.y || 0);

      // 使节点可拖拽
      const drag = d3.drag()
        .on('drag', (event, d: any) => {
          d.x = event.x;
          d.y = event.y;
          d.fx = event.x;
          d.fy = event.y;
          d3.select(event.currentTarget).attr('transform', `translate(${d.x}, ${d.y})`);
          
          // 更新相关连线（简化为中心到中心）
          lines
            .attr('x1', (ld: any) => entities.find(e => e.id === ld.source)?.x || 0)
            .attr('y1', (ld: any) => entities.find(e => e.id === ld.source)?.y || 0)
            .attr('x2', (ld: any) => entities.find(e => e.id === ld.target)?.x || 0)
            .attr('y2', (ld: any) => entities.find(e => e.id === ld.target)?.y || 0);
        });

      nodes.call(drag as any);
    }
  }, [viewMode, layoutMode, showAttributes, showRelationshipLabels, nodeSize, animationEnabled, selectedEntity]);

  // 切换全屏
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // 重置视图
  const resetView = () => {
    const svg = d3.select(svgRef.current);
    svg.transition().duration(750).call(
      d3.zoom().transform as any,
      d3.zoomIdentity
    );
    setZoom(1);
    setSelectedEntity(null);
  };

  // 导出为图片
  const exportAsImage = () => {
    if (!svgRef.current) return;
    
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = svgRef.current.clientWidth;
    canvas.height = svgRef.current.clientHeight;
    
    img.onload = () => {
      ctx!.fillStyle = 'white';
      ctx!.fillRect(0, 0, canvas.width, canvas.height);
      ctx!.drawImage(img, 0, 0);
      
      const a = document.createElement('a');
      a.download = 'entity-diagram.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    message.success('实体关系图已导出');
  };

  useEffect(() => {
    drawDiagram();
  }, [viewMode, layoutMode, showAttributes, showRelationshipLabels, nodeSize, animationEnabled, drawDiagram]);

  return (
    <div ref={containerRef}>
      <Title level={2}>增强版实体关系图</Title>
      <Paragraph>
        结合了简洁和详细两种视图，支持多种布局方式和交互功能
      </Paragraph>

      {/* 控制面板 */}
      <Card 
        title="视图控制" 
        size="small" 
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={resetView}>重置</Button>
            <Button icon={<ZoomOutOutlined />} onClick={exportAsImage}>导出</Button>
            <Button 
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            >
              {isFullscreen ? '退出全屏' : '全屏'}
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <div>
              <div style={{ marginBottom: 8 }}>视图模式</div>
              <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)}>
                <Radio.Button value="simple">简洁视图</Radio.Button>
                <Radio.Button value="detailed">详细视图</Radio.Button>
              </Radio.Group>
            </div>
          </Col>
          
          <Col span={6}>
            <div>
              <div style={{ marginBottom: 8 }}>布局方式</div>
              <Radio.Group value={layoutMode} onChange={e => setLayoutMode(e.target.value)}>
                <Radio.Button value="force">
                  <Tooltip title="自动排列，可拖动">
                    <DragOutlined /> 力导向
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value="manual">
                  <Tooltip title="固定位置，可手动调整">
                    <PushpinOutlined /> 手动
                  </Tooltip>
                </Radio.Button>
              </Radio.Group>
            </div>
          </Col>

          <Col span={6}>
            <div>
              <div style={{ marginBottom: 8 }}>显示选项</div>
              <Space direction="vertical">
                <Checkbox 
                  checked={showAttributes} 
                  onChange={e => setShowAttributes(e.target.checked)}
                  disabled={viewMode === 'simple'}
                >
                  显示属性
                </Checkbox>
                <Checkbox 
                  checked={showRelationshipLabels} 
                  onChange={e => setShowRelationshipLabels(e.target.checked)}
                >
                  显示关系标签
                </Checkbox>
                <Checkbox 
                  checked={animationEnabled} 
                  onChange={e => setAnimationEnabled(e.target.checked)}
                >
                  启用动画
                </Checkbox>
              </Space>
            </div>
          </Col>

          <Col span={6}>
            <div>
              <div style={{ marginBottom: 8 }}>
                节点大小 {viewMode === 'simple' && `(${nodeSize})`}
              </div>
              <Slider
                min={50}
                max={150}
                value={nodeSize}
                onChange={setNodeSize}
                disabled={viewMode === 'detailed'}
              />
              <div style={{ marginTop: 8 }}>
                缩放级别: {(zoom * 100).toFixed(0)}%
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 图例 */}
      <Card title="图例说明" size="small" style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <Tag color="#1890ff">聚合根</Tag>
          <Tag color="#52c41a">实体</Tag>
          <Tag color="#faad14">值对象</Tag>
          <Tag color="#fa8c16">领域服务</Tag>
          <Tag color="#eb2f96">数据库访问层</Tag>
          <Divider type="vertical" />
          <span>
            <span style={{ color: '#ff4d4f' }}>*</span> 必填属性
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            实线带箭头表示关系方向
            <svg width="80" height="20" style={{ marginLeft: '8px' }}>
              <defs>
                <marker
                  id="legend-arrowhead"
                  viewBox="0 -5 10 10"
                  refX="10"
                  refY="0"
                  markerWidth="8"
                  markerHeight="8"
                  orient="auto"
                >
                  <path d="M0,-5L10,0L0,5" fill="#1890ff" stroke="#1890ff" strokeWidth="1" />
                </marker>
              </defs>
              <line x1="5" y1="10" x2="70" y2="10" stroke="#1890ff" strokeWidth="2" markerEnd="url(#legend-arrowhead)" />
            </svg>
          </span>
        </Space>
      </Card>

      {/* 选中实体信息 */}
      {selectedEntity && (
        <Card 
          title={`选中实体: ${entities.find(e => e.id === selectedEntity)?.name}`}
          size="small" 
          style={{ marginBottom: 16 }}
        >
          <Paragraph>
            {chineseNames[entities.find(e => e.id === selectedEntity)?.name || ''] || ''}
            ({typeLabels[entities.find(e => e.id === selectedEntity)?.type || '']})
          </Paragraph>
          <Paragraph>
            相关关系：
            {relationships
              .filter(r => r.source === selectedEntity || r.target === selectedEntity)
              .map(r => r.name)
              .join('、')}
          </Paragraph>
        </Card>
      )}

      {/* 主图表区域 */}
      <Card styles={{ body: { padding: 0, overflow: 'hidden' } }}>
        <svg ref={svgRef} style={{ ...SVG_STYLES.default, width: '100%', minHeight: CHART_DIMENSIONS.minHeight }} />
      </Card>
    </div>
  );
};

export default EnhancedEntityDiagram;